import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { ExploreTabs } from '../components/ExploreTabs';
import { ExploreHero } from '../components/ExploreHero';
import { SiteFooter } from '../components/SiteFooter';
import { ListingCard, type PublicListing } from '../components/ListingCard';
import { useSavedHomes } from '../lib/savedHomes';
import { useApi } from '../lib/useApi';
import { count } from '../lib/format';
import { Button, EmptyState, ErrorBlock, LoadingBlock, Select, TextInput } from '../components/ui';
import '../styles/public.css';
import '../styles/discover.css';

/**
 * Find Your Next Home — the public search.
 *
 * No login, and nothing here knows who is looking. Filters live in the URL
 * rather than in component state so a search can be shared, bookmarked and
 * reached by the back button, which is how people actually use a property
 * search: they send one to someone else.
 */

interface Facets {
  counties: { county: string; n: number }[];
  types: { unit_type: string; n: number }[];
  stays?: { long_term: number; short_stay: number };
}
interface Response {
  listings: PublicListing[];
  total: number; available: number; limit: number; offset: number;
  facets: Facets;
  amenities: { code: string; label: string }[];
}

const BEDROOM_CHOICES = [1, 2, 3, 4, 5];
const GUEST_CHOICES = [1, 2, 4, 6];

export function Discover() {
  const [params, setParams] = useSearchParams();
  // The free-text box is local until submitted; re-querying on every keystroke
  // would make the back button useless and the server busy.
  const [q, setQ] = useState(params.get('q') ?? '');

  const get = (k: string) => params.get(k) ?? '';
  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v); else next.delete(k);
    }
    next.delete('offset');   // a new filter starts at the first page
    setParams(next);
  };

  const offset = Number(get('offset') || 0);
  // Long-term lets or short stays (155). A switch, not a filter: the two are
  // priced in different units, so a rent range carried across would be wrong.
  const short = get('stay') === 'short';
  const switchTo = (toShort: boolean) => {
    if (toShort === short) return;
    const next = new URLSearchParams(params);
    for (const k of ['stay', 'minRent', 'maxRent', 'guests', 'bedrooms', 'offset']) next.delete(k);
    if (toShort) next.set('stay', 'short');
    setParams(next);
  };
  // "Saved" shows the homes this browser saved, and nothing else — the filters
  // step aside rather than silently narrowing a list somebody chose by hand.
  const saved = useSavedHomes();
  const showingSaved = get('saved') === '1';
  const list = useApi<Response>(showingSaved && saved.ids.length === 0 ? null : '/api/v1/discover', showingSaved ? {
    ids: saved.ids.join(','),
  } : {
    q: get('q') || undefined,
    county: get('county') || undefined,
    unitType: get('unitType') || undefined,
    bedrooms: get('bedrooms') || undefined,
    minRent: get('minRent') || undefined,
    maxRent: get('maxRent') || undefined,
    amenities: get('amenities') || undefined,
    stay: short ? 'short_stay' : undefined,
    guests: short ? get('guests') || undefined : undefined,
    offset: offset || undefined,
  });

  const data = list.data;
  const chosenAmenities = get('amenities').split(',').filter(Boolean);
  const toggleAmenity = (code: string) => {
    const next = chosenAmenities.includes(code)
      ? chosenAmenities.filter(a => a !== code)
      : [...chosenAmenities, code];
    set({ amenities: next.join(',') });
  };

  const shown = data?.listings.length ?? 0;
  const filtered = ['q', 'county', 'unitType', 'bedrooms', 'minRent', 'maxRent', 'amenities', 'guests']
    .some(k => get(k));

  return (
    <>
      <PublicHeader />

      {/* Explore's tab strip (spec §3). This page keeps its own URL — see the
          note in ExploreTabs.tsx — and gains the tabs above the hero so a
          visitor can cross to Services without going back to the header. */}
      <div className="dsc-hero-inner" style={{ paddingTop: 16, paddingBottom: 0 }}>
        <ExploreTabs path="/homes" />
      </div>

      {/* The same band as Services and Marketplace (§33: one visual language),
          replacing this page's own flat blue one. The words and the search box
          are unchanged — what it gains is the photographs and the rotating
          line, so the section that carries the most traffic is not the plain
          one. */}
      <div className="dsc-hero-inner" style={{ paddingTop: 0 }}>
        <ExploreHero
          section="homes"
          title="Find your next home"
          sub={short
            ? 'Furnished places to stay by the night, listed by their hosts.'
            : 'Homes to rent across Kenya, listed by the landlords who manage them.'}
        >
          <form className="dsc-search" onSubmit={(e) => { e.preventDefault(); set({ q }); }}>
            <TextInput aria-label="Search by area or description" value={q}
              placeholder="Try an area — Kilimani, Nakuru, Westlands…"
              onChange={(e) => setQ(e.target.value)} />
            <Button type="submit">Search</Button>
          </form>
        </ExploreHero>
      </div>

      {!showingSaved && (
        <div className="dsc-staytabs" role="tablist" aria-label="Kind of stay">
          <button type="button" role="tab" aria-selected={!short}
            className={`dsc-staytab${!short ? ' on' : ''}`} onClick={() => switchTo(false)}>
            Long-term rentals{data?.facets.stays ? ` (${count(data.facets.stays.long_term)})` : ''}
          </button>
          <button type="button" role="tab" aria-selected={short}
            className={`dsc-staytab${short ? ' on' : ''}`} onClick={() => switchTo(true)}>
            Short stays{data?.facets.stays ? ` (${count(data.facets.stays.short_stay)})` : ''}
          </button>
        </div>
      )}

      <div className="dsc-body">
        <aside className="dsc-filters" aria-label="Filters">
          <div className="dsc-filter">
            <label className="dsc-filter-label" htmlFor="f-county">County</label>
            {/* Offered from what is published, so a filter can never propose an
                area with nothing in it. */}
            <Select id="f-county" value={get('county')} onChange={e => set({ county: e.target.value })}>
              <option value="">Anywhere</option>
              {(data?.facets.counties ?? []).map(c => (
                <option key={c.county} value={c.county}>{c.county} ({c.n})</option>
              ))}
            </Select>
          </div>

          <div className="dsc-filter">
            <label className="dsc-filter-label" htmlFor="f-type">Property type</label>
            <Select id="f-type" value={get('unitType')} onChange={e => set({ unitType: e.target.value })}>
              <option value="">Any type</option>
              {(data?.facets.types ?? []).map(t => (
                <option key={t.unit_type} value={t.unit_type}>{t.unit_type} ({t.n})</option>
              ))}
            </Select>
          </div>

          <div className="dsc-filter">
            <span className="dsc-filter-label">Bedrooms</span>
            <div className="dsc-chips">
              {BEDROOM_CHOICES.map(n => (
                <button key={n} type="button"
                  className={`dsc-chip${get('bedrooms') === String(n) ? ' on' : ''}`}
                  aria-pressed={get('bedrooms') === String(n)}
                  onClick={() => set({ bedrooms: get('bedrooms') === String(n) ? '' : String(n) })}>
                  {n}+
                </button>
              ))}
            </div>
          </div>

          {short && (
            <div className="dsc-filter">
              <span className="dsc-filter-label">Guests</span>
              <div className="dsc-chips">
                {GUEST_CHOICES.map(n => (
                  <button key={n} type="button"
                    className={`dsc-chip${get('guests') === String(n) ? ' on' : ''}`}
                    aria-pressed={get('guests') === String(n)}
                    onClick={() => set({ guests: get('guests') === String(n) ? '' : String(n) })}>
                    {n}+
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="dsc-filter">
            <span className="dsc-filter-label">{short ? 'Price per night (KES)' : 'Monthly rent (KES)'}</span>
            <div className="dsc-range">
              <TextInput type="number" inputMode="numeric" aria-label="Minimum rent"
                placeholder="Min" defaultValue={get('minRent')}
                onBlur={e => set({ minRent: e.target.value })} />
              <span className="dsc-range-dash" aria-hidden>–</span>
              <TextInput type="number" inputMode="numeric" aria-label="Maximum rent"
                placeholder="Max" defaultValue={get('maxRent')}
                onBlur={e => set({ maxRent: e.target.value })} />
            </div>
          </div>

          <div className="dsc-filter">
            <span className="dsc-filter-label">Amenities</span>
            <div className="dsc-chips">
              {(data?.amenities ?? []).map(a => (
                <button key={a.code} type="button"
                  className={`dsc-chip${chosenAmenities.includes(a.code) ? ' on' : ''}`}
                  aria-pressed={chosenAmenities.includes(a.code)}
                  onClick={() => toggleAmenity(a.code)}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {filtered && (
            <Button variant="ghost" size="sm" onClick={() => { setQ(''); setParams(new URLSearchParams()); }}>
              Clear all filters
            </Button>
          )}
        </aside>

        <main className="dsc-results">
          <div className="dsc-saved-bar">
            {showingSaved
              ? <Button variant="ghost" size="sm" onClick={() => set({ saved: '' })}>← All homes</Button>
              : (
                <Button variant="secondary" size="sm" onClick={() => set({ saved: '1' })}>
                  ♥ Saved homes{saved.ids.length > 0 ? ` (${saved.ids.length})` : ''}
                </Button>
              )}
          </div>
          {showingSaved && saved.ids.length === 0 ? (
            <EmptyState
              title="No saved homes yet"
              message="Tap the heart on any home to keep it here. Saved homes stay on this device." />
          ) : list.loading ? <LoadingBlock />
            : list.error ? <ErrorBlock error={list.error} onRetry={list.reload} />
            : shown === 0 ? (
              <EmptyState
                title={showingSaved ? 'Your saved homes are no longer listed'
                  : filtered ? 'Nothing matches that search'
                  : short ? 'No short stays listed yet' : 'No homes listed yet'}
                message={filtered
                  ? 'Try a wider rent range, fewer amenities, or a different area.'
                  : 'Listings appear here as landlords publish them. Check back soon.'} />
            ) : (
              <>
                <p className="dsc-count">
                  {/* Counts homes that can actually be enquired on. Taken
                      adverts are still shown below, but promising them as
                      "available" is what marking them taken was meant to end. */}
                  {count(data!.available)} {short ? `place${data!.available === 1 ? '' : 's'} to stay` : `home${data!.available === 1 ? '' : 's'}`} available
                  {data!.total > data!.available && (
                    <span className="muted"> · {count(data!.total - data!.available)} taken</span>
                  )}
                </p>
                <div className="dsc-grid">
                  {data!.listings.map(l => <ListingCard key={l.id} listing={l} />)}
                </div>

                {data!.total > shown + offset && (
                  <div className="dsc-more">
                    <Button variant="secondary"
                      onClick={() => set({ offset: String(offset + data!.limit) })}>
                      Show more
                    </Button>
                  </div>
                )}
                {offset > 0 && (
                  <div className="dsc-more">
                    <Button variant="ghost" size="sm"
                      onClick={() => set({ offset: String(Math.max(0, offset - data!.limit)) })}>
                      Back a page
                    </Button>
                  </div>
                )}
              </>
            )}
        </main>
      </div>

      {/* The other half of the marketplace: every visitor is a potential
          landlord, and this is the cheapest place to say so. */}
      <section className="dsc-cta">
        <h2>Have a home to let or sell?</h2>
        <p>Owners, agents and caretakers can post it here. Every advert is checked by us before it goes live.</p>
        <Link className="btn btn-primary" to="/post">Post a property</Link>
      </section>

      <SiteFooter />
    </>
  );
}
